import "./index.css";
import { ClosedBusinessTabs } from "./components/Tabs/ClosedBusinessTabs";
import { OpenBusinessTabs } from "./components/Tabs/OpenBusinessTabs";
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Board from "./pages/Board";
import GovernanceTab from "./components/Governance/GovernanceTab";
import MeetingSection from "./components/Board/MeetingSection";
import TabHeroCard from "./components/Governance/TabHeroCard";
import DocumentFolders from "./components/Documents/DocumentFolders";
import StorageUpgradeModal from "../../components/Storage/StorageUpgradeModal";
import { useUserModules } from "../../components/UserModuleContext";
import { useNavigate } from "react-router-dom";
import boardIcon from "../../assets/business-dash/tabs-icons/board-icon-business.svg";
import strategiIcon from "../../assets/business-dash/tabs-icons/strategi-icon-business.svg";
import { toast } from "sonner";
import accessService from "../../apis/Profile/AccessServiceApi";
import {
  BUSINESS_DASH_NAVIGATE,
  MEETING_HUB_FOCUS,
  BOARD_INTRINSIC_NAVIGATE,
  SESSION_STRATEGY_ATTENTION,
} from "./lib/boardActionsNavigate";

const queryClient = new QueryClient();

const BusinessDash = () => {
  const { t } = useTranslation();
  const {
    isBoardMember,
    isLoading,
    isAccessManager,
  } = useUserModules();
  const navigate = useNavigate();
  const [open, setOpen] = useState("board");
  const [isStorageModalOpen, setIsStorageModalOpen] = useState(false);
  const meetingTabRef = useRef(null);
  const [navigateToDocId, setNavigateToDocId] = useState(null);
  const [strategyContext, setStrategyContext] = useState(null);

  // Meeting tab mounts visible only when selected — nudge panels after layout (fixes first paint in any language)
  useLayoutEffect(() => {
    if (open !== "meeting") return;
    let alive = true;
    const nudgeResize = () => {
      if (!alive) return;
      window.dispatchEvent(new Event("resize"));
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(nudgeResize);
    });
    const t = setTimeout(nudgeResize, 0);
    const t2 = setTimeout(nudgeResize, 150);
    const el = meetingTabRef.current;
    const ro =
      el &&
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => nudgeResize())
        : null;
    if (el && ro) ro.observe(el);
    return () => {
      alive = false;
      clearTimeout(t);
      clearTimeout(t2);
      ro?.disconnect();
    };
  }, [open]);

  useEffect(() => {
    const onNavigate = (e) => {
      const d = e.detail || {};
      const tab =
        d.openChemistryAssessment && d.tab === "board"
          ? "governance"
          : d.tab;

      const wantsGovernanceNav =
        tab === "governance" ||
        d.governanceSection != null ||
        d.openChemistryAssessment ||
        d.openPulseAssessment;

      if (wantsGovernanceNav) {
        try {
          let section = d.governanceSection;
          if (!section) {
            if (d.openPulseAssessment) section = "pulse";
            else section = "chemistry";
          }
          if (
            section !== "chemistry" &&
            section !== "pulse" &&
            section !== "training"
          ) {
            section = "chemistry";
          }
          sessionStorage.setItem(
            "az_governance_nav",
            JSON.stringify({
              section,
              autoStartChemistry: !!d.openChemistryAssessment,
              autoStartPulse: !!d.openPulseAssessment,
            }),
          );
        } catch {
          /* ignore */
        }
      }

      if (tab) setOpen(tab);
      if (d.navigateToDocumentId != null && d.navigateToDocumentId !== "") {
        setNavigateToDocId(String(d.navigateToDocumentId));
      }
      if (d.strategyContext && typeof d.strategyContext === "object") {
        setStrategyContext(d.strategyContext);
      }
      window.setTimeout(() => {
        if (tab === "meeting") {
          window.dispatchEvent(new Event(MEETING_HUB_FOCUS));
        }
        if (tab === "board") {
          window.dispatchEvent(
            new CustomEvent(BOARD_INTRINSIC_NAVIGATE, { detail: d }),
          );
        }
        if (tab === "strategi") {
          try {
            const aid = sessionStorage.getItem(SESSION_STRATEGY_ATTENTION);
            if (aid) {
              sessionStorage.removeItem(SESSION_STRATEGY_ATTENTION);
              toast.info("Strategy", {
                description: `Attention item #${aid} — open the full Strategy workspace when it is wired to this item.`,
              });
            }
          } catch {
            /* ignore */
          }
        }
      }, 100);
    };
    window.addEventListener(BUSINESS_DASH_NAVIGATE, onNavigate);
    return () => window.removeEventListener(BUSINESS_DASH_NAVIGATE, onNavigate);
  }, []);

  const handleClick = (name) => {
    setOpen(name);
  };

  const tabs = [
    { id: 1, key: "board",      label: t("BOARD"),    icon: boardIcon   },
    { id: 2, key: "strategi",   label: t("STRATEGI"), icon: strategiIcon },
    { id: 3, key: "meeting",    label: t("MEETING"),  icon: strategiIcon },
    { id: 4, key: "documents",  label: "Documents",   icon: strategiIcon },
    { id: 5, key: "governance", label: "Governance",  icon: strategiIcon },
  ];

  const renderTabContent = () => {
    switch (open) {
      case "board":
        return (
          <Board
            onStorageLimitReached={(info) =>
              handleStorageLimitReached(info, "board")
            }
          />
        );
      case "strategi":
        return (
          <div className="tab-content-placeholder">
            <p>Strategi content coming soon...</p>
          </div>
        );
      case "meeting":
        return <MeetingSection isTabActive={open === "meeting"} />;
      case "documents":
        return (
          <div className="p-6">
            <TabHeroCard
              title="DOCUMENTS"
              icon={
                <svg
                  className="w-6 h-10 text-white"
                  viewBox="0 0 16 28"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden
                >
                  <rect x="1" y="1" width="14" height="26" rx="2" ry="2" />
                  <path d="M4 6 L12 6" />
                  <path d="M4 10 L12 10" />
                  <path d="M4 14 L10 14" />
                  <path d="M4 18 L12 18" />
                  <path d="M4 22 L8 22" />
                </svg>
              }
              headline="Where are the important documents?"
              description="Centralized document management for contracts, policies, leases, and governance materials. Share documents internally or externally, and track digital signatures via Penneo."
              subdescription="Chair-restricted folders ensure sensitive governance documents remain appropriately secured."
            />
            <DocumentFolders
              dashboardContext="business"
              navigateToDocumentId={navigateToDocId}
              onDocumentNavigated={() => {
                setNavigateToDocId(null);
                setStrategyContext(null);
              }}
              strategyContext={strategyContext}
              onBackToStrategy={() => {
                setOpen("strategi");
                setStrategyContext(null);
                try {
                  window.history.replaceState({}, "", "/business-dash");
                } catch {
                  /* ignore */
                }
              }}
              onStorageLimitReached={handleStorageLimitReached}
            />
          </div>
        );
      case "governance":
        return (
          <div className="p-6">
            <GovernanceTab />
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    // Redirect non-board members
    if (!isLoading && !isBoardMember) {
      navigate("/");
    }
  }, [isBoardMember, isLoading, navigate]);

  const handleStorageLimitReached = (info) => {
    if (info?.error) {
      toast.error(info.error, {
        description: `You are using ${info.used_gb} GB of ${info.limit_gb} GB.`,
      });
    } else {
      toast.warning("You are approaching the 1 GB storage limit.");
    }
    setIsStorageModalOpen(true);
  };

  const handleNotifyAccessManager = async () => {
    try {
      await accessService.createUpgradeRequest({ reason: "storage_addon" });
      toast.success("We sent a notification to your Access Manager.");
    } catch (error) {
      console.error("Failed to request storage upgrade:", error);
      toast.error(
        error?.error || "Failed to contact your Access Manager. Try again later."
      );
    }
  };

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <div className="business-dash-container">
        <div className="loading-state">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not a board member (will redirect)
  if (!isBoardMember) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <div className="business-dash-container">
          <div className="business-tabs-container">
            {tabs.map((item) => {
              const isActive = open === item.key;
              return isActive ? (
                <OpenBusinessTabs
                  name={item.label}
                  key={item.id}
                  onClick={() => handleClick(item.key)}
                  icon={item.icon}
                />
              ) : (
                <ClosedBusinessTabs
                  name={item.label}
                  key={item.id}
                  onClick={() => handleClick(item.key)}
                />
              );
            })}
          </div>

          {open && (
            <div
              ref={open === "meeting" ? meetingTabRef : undefined}
              className="business-tab-content"
            >
              {renderTabContent()}
            </div>
          )}
        </div>
        <StorageUpgradeModal
          isOpen={isStorageModalOpen}
          onClose={() => setIsStorageModalOpen(false)}
          isAccessManager={isAccessManager}
          onRequestUpgrade={handleNotifyAccessManager}
        />
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default BusinessDash;

